import { useState, useMemo } from 'react';
import type { 
  Order, 
  OrderItem, 
  PaymentType, 
  SplitPaymentDetails,
  SplitPaymentItem
} from '@/types/common';
import { formatCurrency } from '@/utils/format';

export interface SplitPaymentProps {
  order: Order;
  orderItems: OrderItem[];
  status: 'idle' | 'processing' | 'completed' | 'error';
  onSubmit: (splits: SplitPaymentDetails[]) => Promise<void>;
}

interface Split {
  id: string;
  items: SplitPaymentItem[];
  tipAmount: string;
  customAmount: string;
  payment_type: PaymentType;
}

export default function SplitPayment({ order, orderItems, status, onSubmit }: SplitPaymentProps) {
  const [splits, setSplits] = useState<Split[]>([
    {
      id: '1',
      items: orderItems.map(item => ({
        id: item.id,
        amount: (item.menu_item?.price || 0) * item.quantity,
        quantity: item.quantity
      })),
      tipAmount: '0',
      customAmount: '0',
      payment_type: 'cash',
    },
  ]);

  const remainingItems = useMemo(() => {
    const assignedItems = new Set(splits.flatMap(split => split.items.map(item => item.id)));
    return orderItems.filter(item => !assignedItems.has(item.id));
  }, [orderItems, splits]);

  const tipPercentages = [0, 5, 10, 15, 20];

  const calculateSplitTotal = (split: Split) => {
    const itemsTotal = split.items.reduce((sum, item) => sum + item.amount, 0);
    const tip = parseFloat(split.tipAmount) || 0;
    const custom = parseFloat(split.customAmount) || 0;
    return itemsTotal + tip;
  };

  const addSplit = () => {
    if (remainingItems.length === 0) return;

    setSplits([
      ...splits,
      {
        id: (splits.length + 1).toString(),
        items: [],
        tipAmount: '0',
        customAmount: '0',
        payment_type: 'cash',
      },
    ]);
  };

  const removeSplit = (id: string) => {
    if (splits.length === 1) return;

    // Return items from this split to remaining items pool
    const splitToRemove = splits.find(s => s.id === id);
    if (!splitToRemove) return;

    setSplits(splits.filter(split => split.id !== id));
  };

  const updateSplit = (id: string, updates: Partial<Split>) => {
    setSplits(splits.map(split => split.id === id ? { ...split, ...updates } : split));
  };

  const handleItemToggle = (splitId: string, item: OrderItem) => {
    const splitPaymentItem: SplitPaymentItem = {
      id: item.id,
      amount: (item.menu_item?.price || 0) * item.quantity,
      quantity: item.quantity
    };

    const sourceSplit = splits.find(s => s.items.some(i => i.id === item.id));
    const targetSplit = splits.find(s => s.id === splitId);

    if (!targetSplit) return;

    if (sourceSplit?.id === splitId) {
      // Uncheck: Remove from current split
      updateSplit(splitId, {
        items: targetSplit.items.filter(i => i.id !== item.id)
      });
    } else {
      // Check: Move item to target split
      if (sourceSplit) {
        // Remove from source split
        updateSplit(sourceSplit.id, {
          items: sourceSplit.items.filter(i => i.id !== item.id)
        });
      }
      // Add to target split
      updateSplit(splitId, {
        items: [...targetSplit.items, splitPaymentItem]
      });
    }
  };

  const handleTipSelect = (splitId: string, percentage: number) => {
    const split = splits.find(s => s.id === splitId);
    if (!split) return;

    const itemsTotal = split.items.reduce((sum, item) => sum + item.amount, 0);
    const tipValue = (itemsTotal * percentage) / 100;
    updateSplit(splitId, { tipAmount: tipValue.toFixed(2) });
  };

  const getTotalAmount = () => {
    return splits.reduce((sum, split) => sum + calculateSplitTotal(split), 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(
      splits.map((split) => ({
        items: split.items,
        payment_type: split.payment_type,
        amount: calculateSplitTotal(split),
        tip_amount: split.tipAmount ? parseFloat(parseFloat(split.tipAmount).toFixed(2)) : 0,
        custom_amount: split.customAmount ? parseFloat(parseFloat(split.customAmount).toFixed(2)) : 0
      }))
    );
  };

  const isFormValid = () => {
    const allItemsAssigned = orderItems.every(item => 
      splits.some(split => split.items.some(i => i.id === item.id))
    );
    
    const totalAmount = getTotalAmount();
    const isValidTotal = totalAmount >= order.total;
    
    return allItemsAssigned && isValidTotal && status === 'idle';
  };

  const [expandedSections, setExpandedSections] = useState<{
    [splitId: string]: {
      tip: boolean;
      custom: boolean;
    };
  }>({});

  const toggleSection = (splitId: string, section: 'tip' | 'custom') => {
    setExpandedSections(prev => ({
      ...prev,
      [splitId]: {
        ...prev[splitId],
        [section]: !prev[splitId]?.[section]
      }
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {splits.map((split, index) => (
          <div
            key={split.id}
            className="flex flex-col space-y-4 p-4 bg-gray-50 dark:bg-dark-bg-lighter rounded-lg"
          >
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                Split #{index + 1}
              </h3>
              {splits.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeSplit(split.id)}
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  disabled={status === 'processing'}
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>

            {/* Item Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Select Items
              </label>
              <div className="space-y-2">
                {orderItems.map(item => {
                  const isAssignedToThisSplit = split.items.some(i => i.id === item.id);
                  const isAssignedToOtherSplit = splits.some(s => 
                    s.id !== split.id && s.items.some(i => i.id === item.id)
                  );

                  if (isAssignedToOtherSplit) {
                    return null;
                  }

                  return (
                    <label
                      key={item.id}
                      className="flex items-center p-2 rounded hover:bg-gray-100 dark:hover:bg-dark-bg-lighter cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isAssignedToThisSplit}
                        onChange={() => handleItemToggle(split.id, item)}
                        disabled={status === 'processing'}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-dark-text-secondary">
                        {item.menu_item?.name} ({item.quantity}x) - {formatCurrency((item.menu_item?.price || 0) * item.quantity)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Payment Method
              </label>
              <select
                value={split.payment_type}
                onChange={(e) => {
                  const newPaymentType = e.target.value as PaymentType;
                  updateSplit(split.id, { 
                    payment_type: newPaymentType,
                    // Reset custom amount when switching to card payment
                    ...(newPaymentType === 'card' ? { customAmount: '0' } : {})
                  });
                }}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-dark-border focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-dark-bg-light dark:text-dark-text-primary"
                disabled={status === 'processing'}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
              </select>
            </div>

            {/* Tip Selection */}
            <div>
              <button
                type="button"
                onClick={() => toggleSection(split.id, 'tip')}
                className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2"
              >
                <span>Add Tip</span>
                <i className={`fas fa-chevron-${expandedSections[split.id]?.tip ? 'up' : 'down'}`} />
              </button>
              {expandedSections[split.id]?.tip && (
                <div className="space-y-2">
                  <div className="flex gap-2 mb-2">
                    {tipPercentages.map((percentage) => (
                      <button
                        key={percentage}
                        type="button"
                        onClick={() => handleTipSelect(split.id, percentage)}
                        disabled={status === 'processing'}
                        className={`flex-1 py-2 px-3 text-sm font-medium rounded-md ${
                          split.tipAmount === ((calculateSplitTotal(split) * percentage) / 100).toFixed(2)
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                            : 'bg-white dark:bg-dark-bg-light text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-lighter'
                        } border border-gray-300 dark:border-dark-border`}
                      >
                        {percentage ? `${percentage}%` : 'No Tip'}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-dark-text-secondary">
                      €
                    </span>
                    <input
                      type="number"
                      value={split.tipAmount}
                      onChange={(e) => updateSplit(split.id, { tipAmount: e.target.value })}
                      placeholder="0.00"
                      disabled={status === 'processing'}
                      className="w-full h-11 pl-7 pr-4 rounded-lg border border-gray-300 dark:border-dark-border dark:bg-dark-bg-lighter dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Custom Amount */}
            {split.payment_type === 'cash' && (
              <div>
                <button
                  type="button"
                  onClick={() => toggleSection(split.id, 'custom')}
                  className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2"
                >
                  <span>Custom Amount</span>
                  <i className={`fas fa-chevron-${expandedSections[split.id]?.custom ? 'up' : 'down'}`} />
                </button>
                {expandedSections[split.id]?.custom && (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-dark-text-secondary">
                      €
                    </span>
                    <input
                      type="number"
                      value={split.customAmount}
                      onChange={(e) => updateSplit(split.id, { customAmount: e.target.value })}
                      placeholder="0.00"
                      disabled={status === 'processing'}
                      className="w-full h-11 pl-7 pr-4 rounded-lg border border-gray-300 dark:border-dark-border dark:bg-dark-bg-lighter dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                      step="0.01"
                      min="0"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Split Total */}
            <div className="pt-4 border-t border-gray-200 dark:border-dark-border">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary">
                  Split Total:
                </span>
                <span className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                  {formatCurrency(calculateSplitTotal(split))}
                </span>
              </div>
            </div>
          </div>
        ))}

        {remainingItems.length > 0 && splits.every(split => split.items.length > 0) && (
          <button
            type="button"
            onClick={addSplit}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-dark-border shadow-sm text-sm font-medium rounded text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-light hover:bg-gray-50 dark:hover:bg-dark-bg-lighter focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            disabled={status === 'processing'}
          >
            <i className="fas fa-plus mr-2" />
            Add Split
          </button>
        )}
      </div>

      {/* Total Summary */}
      <div className="flex justify-between text-sm p-4 bg-gray-50 dark:bg-dark-bg-lighter rounded-lg">
        <span className="text-base font-medium text-gray-900 dark:text-dark-text-primary">Total Amount</span>
        <span className={`text-base font-medium ${
          getTotalAmount() >= order.total
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400'
        }`}>
          {formatCurrency(getTotalAmount())} / {formatCurrency(order.total)}
        </span>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!isFormValid() || status !== 'idle'}
        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {status === 'processing' ? (
          <>
            <i className="fas fa-circle-notch fa-spin mr-2" />
            Processing...
          </>
        ) : status === 'completed' ? (
          <>
            <i className="fas fa-check mr-2" />
            Payment Complete
          </>
        ) : (
          <>
            <i className="fas fa-money-bill-wave mr-2" />
            Process Split Payment
          </>
        )}
      </button>
    </form>
  );
}